import { Injectable } from '@angular/core';
import { Observable, of, delay, tap } from 'rxjs';
import { BatchService } from './batch.service';
import { Batch } from '../../interfaces/batch/batch.interface';
import { BatchSearchParam } from '../../interfaces/batch/batch-search-param.interface';
import { PageResponse } from '../../../../core/interfaces/page-response.interface';

@Injectable({
    providedIn: 'root'
})
export class BatchMockService extends BatchService {

    private mockBatches: Batch[] = [
        {
            id: 'BATCH123',
            uniqueId: 'BATCH123',
            institutionUniqueId: 'INST456',
            institutionName: 'ABC Engineering College',
            batchName: '2024 CSE Batch',
            name: '2024 CSE Batch',
            academicYear: '2020',
            academicEndYear: '2024',
            degree: 'B.E',
            department: 'Computer Science and Engineering',
            expectedGraduation: '2024-05-30',
            graduationDate: '2024-05-30',
            expectedBatchSize: 120,
            status: 'ACTIVE',
            invitationMethod: 'EMAIL',
            enrolledStudents: 100,
            placedStudents: 75,
            placementPercentage: 75.00,
            invitesSent: 110,
            institutionId: 'inst-456'
        },
        {
            id: 'batch-1',
            uniqueId: 'BATCH001',
            batchName: '2024 Alpha - Computer Science',
            name: '2024 Alpha - Computer Science',
            department: 'Computer Science and Engineering',
            departmentCode: 'CSE',
            degree: 'B.E',
            institutionId: 'inst-123',
            institutionUniqueId: 'INST001',
            createdAt: 'Oct 12, 2023',
            intakeId: 'CS-24A',
            academicYear: '2020',
            expectedGraduation: '2024-05-30',
            graduationDate: '2024-05-30',
            expectedBatchSize: 120,
            status: 'Active',
            invitationMethod: 'EMAIL',
            enrolledStudents: 120,
            placedStudents: 85,
            placementPercentage: 71,
            invitesSent: 120
        },
        {
            id: 'batch-2',
            uniqueId: 'BATCH002',
            batchName: '2024 Beta - Information Tech',
            name: '2024 Beta - Information Tech',
            department: 'Information Technology',
            departmentCode: 'IT',
            degree: 'B.E',
            institutionId: 'inst-123',
            institutionUniqueId: 'INST001',
            createdAt: 'Nov 05, 2023',
            intakeId: 'IT-24B',
            academicYear: '2020',
            expectedGraduation: '2024-05-30',
            graduationDate: '2024-05-30',
            expectedBatchSize: 95,
            status: 'Active',
            invitationMethod: 'EMAIL',
            enrolledStudents: 95,
            placedStudents: 70,
            placementPercentage: 74,
            invitesSent: 95
        }
    ];

    constructor() {
        super();
    }

    override findAll(searchParam: BatchSearchParam): Observable<PageResponse<Batch>> {
        return this.wrapRequest(
            of({
                content: this.mockBatches,
                page: {
                    number: searchParam.page || 0,
                    size: searchParam.size || 10,
                    totalElements: this.mockBatches.length,
                    totalPages: Math.ceil(this.mockBatches.length / (searchParam.size || 10))
                }
            }).pipe(delay(1000)),
            false,
            'Failed to fetch batches'
        );
    }

    override find(id: string): Observable<Batch> {
        const batch = this.mockBatches.find(b => b.id === id || b.uniqueId === id);
        return this.wrapRequest(
            of(batch!).pipe(delay(1000)),
            false,
            'Failed to fetch batch'
        );
    }

    override create(data: Partial<Batch>): Observable<Batch> {
        const newBatch = { ...data, id: 'batch-' + Math.random().toString(36).substring(7) } as Batch;
        this.mockBatches.push(newBatch);

        return this.wrapRequest(
            of(newBatch).pipe(delay(1500)).pipe(tap(() => {
                this.toast.success('Batch created successfully');
            })),
            false,
            'Failed to create batch'
        );
    }

    override update(id: string, data: Partial<Batch>): Observable<Batch> {
        const index = this.mockBatches.findIndex(b => b.id === id);
        if (index !== -1) {
            this.mockBatches[index] = { ...this.mockBatches[index], ...data } as Batch;
        }
        return this.wrapRequest(
            of(this.mockBatches[index]).pipe(delay(1500)).pipe(tap(() => {
                this.toast.success('Batch updated successfully');
            })),
            false,
            'Failed to update batch'
        );
    }

    override delete(id: string): Observable<void> {
        this.mockBatches = this.mockBatches.filter(b => b.id !== id);
        return this.wrapRequest(
            of(undefined).pipe(delay(1000)).pipe(tap(() => {
                this.toast.success('Batch deleted successfully');
            })),
            false,
            'Failed to delete batch'
        );
    }

    override uploadStudents(institutionUniqueId: string, institutionBatchUniqueId: string, file: File): Observable<any> {
        return this.wrapRequest(
            of({ success: true, message: 'Students uploaded successfully (mock)' }).pipe(delay(2000)).pipe(tap(() => {
                this.toast.success('Students uploaded successfully');
            })),
            false,
            'Failed to upload students'
        );
    }
}
